import { useSearchParams } from "react-router";
import {
  BusinessCalendar,
  parseDate,
  parseView,
  type View,
} from "@/design/BusinessCalendar";
import { PageShell } from "@/design/ui";
import type { BookingView } from "@/modules/booking/surfaces";
import type { TimeBlock } from "@/modules/scheduling/surfaces";
import { AppLink } from "~/components/AppLink";
import { BusinessPage } from "~/components/BusinessChrome";
import { ScreenPending } from "~/components/ScreenPending";
import { useQuery } from "~/lib/useQuery";

/**
 * BusinessCalendarScreen (docs/ux/screens/BusinessCalendarScreen.md).
 *
 * **Read-only**, which is why it has no command adapters at all: the calendar
 * shows the shape of the week, and deciding happens on the queue. The "List"
 * control at the top is the way across.
 *
 * **The URL is what says what is on screen** (`?view=&date=&practitioner=`), so
 * every control is a plain link, the back button works through a month of
 * navigation, and a colleague can be sent "look at Thursday". The client parses
 * that URL with the **same** helpers the API does, so the two cannot disagree
 * about which week a date belongs to.
 *
 * The cursor and the resolved filter come back **in the payload** rather than
 * being assumed from the URL: the server decides the practitioner default from
 * the tier (H26) and falls back to "all" for anything it does not recognise, so
 * what it filtered by is the only trustworthy answer to what is shown.
 */
interface CalendarPayload {
  view: View;
  cursor: string;
  today: string;
  practitioners: { id: string; displayName: string }[];
  practitionerFilter: string;
  bookings: BookingView[];
  patterns: Record<string, TimeBlock[]>;
}

export function BusinessCalendarScreen() {
  const [params] = useSearchParams();
  const search = params.toString();
  const calendar = useQuery<CalendarPayload>(
    `/business/calendar${search ? `?${search}` : ""}`,
  );

  // Parsed locally too, so the controls render the right window on the first
  // paint rather than jumping when the answer lands. The server's copy wins
  // once it arrives, because it is the one the query actually used.
  const localView = parseView(params.get("view"));
  const data = calendar.data;

  return (
    <BusinessPage>
      <PageShell>
        <div className="mx-auto w-full max-w-6xl">
          {calendar.loading && !data ? (
            <ScreenPending />
          ) : (
            <BusinessCalendar
              view={data?.view ?? localView}
              cursor={data?.cursor ?? parseDate(params.get("date"), "")}
              today={data?.today ?? ""}
              practitioners={data?.practitioners ?? []}
              practitionerFilter={data?.practitionerFilter ?? "all"}
              bookings={data?.bookings ?? null}
              patterns={data?.patterns ?? {}}
              configured={calendar.failure !== "not_configured"}
              Link={AppLink}
            />
          )}
        </div>
      </PageShell>
    </BusinessPage>
  );
}
